import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Phone, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { v: "cost_segregation", l: "Cost Segregation" },
  { v: "rd_tax_credit", l: "R&D Tax Credit" },
  { v: "both", l: "Both / Not Sure" },
  { v: "cpa_partnership", l: "CPA Partnership" },
  { v: "other", l: "Something Else" },
];

const AUDIENCE_OPTIONS = [
  { v: "real_estate_investor", l: "Real Estate Investor" },
  { v: "business_owner", l: "Business Owner" },
  { v: "private_equity", l: "Private Equity / Fund" },
  { v: "cpa", l: "CPA / Advisor" },
  { v: "other", l: "Other" },
];

export default function Contact({ estimatorSnapshot }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    firm: "",
    audience: "real_estate_investor",
    service_interest: "both",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        estimator_snapshot: estimatorSnapshot || null,
      });
      setDone(true);
      toast.success("Thanks — an advisor will reach out within 1 business day.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Submission failed. Please try again.";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-ivory-200">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-forest-600 uppercase tracking-[0.24em] text-sm mb-4">
            Talk to an advisor
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-forest-950 leading-[1.05] tracking-tight mb-6">
            A 30-minute call. <br />
            <span className="italic text-brass">A real number.</span>
          </h2>
          <p className="text-forest-800 text-lg leading-relaxed mb-10">
            Tell us a little about your situation. A senior advisor (not a sales rep) will follow up within one business day with a concrete estimate.
          </p>

          <ul className="space-y-4 text-forest-900">
            <li className="flex items-center gap-3">
              <Mail className="text-brass" size={18} />
              <a href="mailto:info@incenttax.com" className="hover:text-brass">info@incenttax.com</a>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="text-brass" size={18} />
              <a href="tel:+18888541637" className="hover:text-brass">1-888-854-1637</a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="text-brass mt-1 shrink-0" size={18} />
              <span>24 West Camelback Road #467<br/>Phoenix, AZ 85013</span>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div
              data-testid="contact-success"
              className="bg-forest-950 text-ivory p-12 h-full flex flex-col justify-center"
            >
              <p className="text-brass uppercase tracking-[0.24em] text-sm mb-4">Received</p>
              <h3 className="font-serif text-4xl text-ivory mb-4">Thank you, {form.name.split(" ")[0]}.</h3>
              <p className="text-ivory/80 text-lg leading-relaxed">
                A senior advisor will be in touch within one business day. If your situation is time-sensitive, call us at <a className="text-brass underline" href="tel:+18888541637">1-888-854-1637</a> or email <a className="text-brass underline" href="mailto:info@incenttax.com">info@incenttax.com</a>.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-ivory-400 p-8 sm:p-12">
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Full Name *">
                  <input
                    required
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    data-testid="contact-name"
                    className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass"
                  />
                </Field>
                <Field label="Email *">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid="contact-email"
                    className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    data-testid="contact-phone"
                    className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass"
                  />
                </Field>
                <Field label="Firm / Company">
                  <input
                    value={form.firm}
                    onChange={(e) => update("firm", e.target.value)}
                    data-testid="contact-firm"
                    className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass"
                  />
                </Field>
                <Field label="I am a...">
                  <select
                    value={form.audience}
                    onChange={(e) => update("audience", e.target.value)}
                    data-testid="contact-audience"
                    className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass"
                  >
                    {AUDIENCE_OPTIONS.map((o) => (
                      <option key={o.v} value={o.v}>{o.l}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Service Interest">
                  <select
                    value={form.service_interest}
                    onChange={(e) => update("service_interest", e.target.value)}
                    data-testid="contact-service"
                    className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass"
                  >
                    {SERVICE_OPTIONS.map((o) => (
                      <option key={o.v} value={o.v}>{o.l}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Tell us about your situation" className="mt-6">
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Property type & cost basis, business activities, or anything else helpful…"
                  data-testid="contact-message"
                  className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass resize-none"
                />
              </Field>

              {estimatorSnapshot && (
                <div className="mt-4 text-xs text-forest-600 uppercase tracking-[0.2em]">
                  Estimator snapshot will be attached.
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-10 py-4 font-medium transition-colors disabled:opacity-60"
                data-testid="contact-submit"
              >
                {submitting ? <Loader2 className="animate-spin" size={18} /> : null}
                Request My Free Review
              </button>

              <p className="mt-4 text-xs text-forest-600">
                We respond within 1 business day. Your info is never sold or shared.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-forest-600 uppercase tracking-[0.2em] text-xs mb-3">
        {label}
      </span>
      {children}
    </label>
  );
}
